import React, { useState, useEffect, FunctionComponent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../../common/contexts/UserContext";
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext";
import ComponentWrapper from "../../common/components/ComponentWrapper";
import Button from "@murphy-frontend/web-core/components/Button";
import { useForm } from "react-hook-form";
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { useCustomer } from "../../common/contexts/CustomerContext";
import Spinner from "@murphy-frontend/web-core/components/Spinner";
import TabPanel from "@murphy-frontend/web-core/components/Tabs/TabPanel";
import IncidentBasicInformation from "./components/IncidentBasicInformation";
import { AlertTemplateDTO } from "../TemplateCreator/apiAlert/models";
import { useGetTemplate } from "./apiEventflow/queries";
import { useGetUsers } from "@murphy-frontend/common/api/Users/Queries"
import EditContact from "../Contacts/components/EditContact"
import { useGetContactGroups } from "@murphy-frontend/common/api/ContactGroups/Queries"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { getAlertTemplates2 } from "./apiAlert/queries";
import * as yup from "yup";
import {MBSRecipient} from "./apiAlert/models";
import {
  useCreateTemplate,
  useUpdateTemplate,
  useCreateSendAlertAction,
	useUpdateSendAlertAction,
} from "./apiEventflow/mutations";
import {
  CreateEventFlowTemplateDto,
  UpdateEventFlowTemplateDto,
  EventFlowTemplateDto,
  EventFlowTemplatePermissionsDTO,
  EventFlowAlertActionDto,
} from "./apiEventflow/models";
import ContactList, { ContactListModel } from "../Contacts/components/ContactList"
import {
	ContactModel,
	OptionSetModel,
	OptionSetViewModel,
	SelectedOptionSetViewModel
} from "../../common/api/Contacts/ContactsApi"
import CreateComponents from "./components/createcomponents";
import TemplatePermissionSelector from "./components/TemplatePermissionSelector";


export interface EventFlowTemplateFormDto {
  Id: number;
  CustomerId: string;
  Name: string;
  Description: string;
  Type: number;
  GeoLocationEnabled: boolean;
  ConfirmBeforeStart: boolean;
  permissions?: EventFlowTemplatePermissionsDTO[];
  isPermissionActive: boolean;
  AlertTemplateId?: number;
}

const CreateEventFlowTemplate: FunctionComponent = () => {
  const navigate = useNavigate();
  const { translations } = useUserPreferences();
  const { openModal } = useModal();
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const templateIdParam = searchParams.get("templateId");
  const templateId = templateIdParam ? parseInt(templateIdParam, 10) : null;
  const [selectedTabId, setSelectedTabId] = useState<number>(1);
  const { customer } = useCustomer();
  const [users, setUsers] = useState<MBSRecipient[]>([]);
  const [notSelectedRecipients, setNotSelectedRecipients] = useState<MBSRecipient[]>([]);
  const [contactGroups, setContactGroups] = useState<MBSRecipient[]>([]);
  const {
    data: dataUsers,
  } = useGetUsers(customer.Id);
  const {
    data: dataContactGroups,
  } = useGetContactGroups(customer.Id);

  const { control, setValue, getValues } = useForm<EventFlowTemplateFormDto>({
    defaultValues: {
      AlertTemplateId: null,
      Type: null
    }
  });

  const { mutate: createTemplate } = useCreateTemplate(customer.Id);
  const { mutate: updateTemplate } = useUpdateTemplate(customer.Id);
  const { data: alertTemplates } = getAlertTemplates2(customer.Id);
  const { data: existingTemplate } = useGetTemplate(templateId ?? 0);

  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [confirmBeforeStart, setConfirmBeforeStart] = useState(true);
  const [addPermissions, setAddPermissions] = useState(false);
  const [geoLocation, setGeoLocation] = useState(false);
  const {mutate: createSendAlertAction} = useCreateSendAlertAction();
  const {mutate: updateSendAlertAction} = useUpdateSendAlertAction();

  const alertTemplateOptions = alertTemplates?.filter(p => p.Enabled && p.EventFlowEnabled)
    .map((alertTemplate: AlertTemplateDTO) => ({
      value: alertTemplate.Id,
      label: alertTemplate.Name,
    })) ?? [];

  const [basicInitialized, setBasicInitialized] = useState(false);
  useEffect(() => {
    if (existingTemplate && !basicInitialized) {
      setTemplateName(existingTemplate.Name);
      setTemplateDescription(existingTemplate.Description);
      setGeoLocation(existingTemplate.GeoLocationEnabled);
      setConfirmBeforeStart(existingTemplate.ConfirmBeforeStart);
      setAddPermissions(existingTemplate.isPermissionActive);

      if (existingTemplate.Type) {
        setValue("Type", existingTemplate.Type);
      }

      const alertAction = existingTemplate.Actions?.find(a => a.EventType === 1);
      if (alertAction) {
        try {
          const config = JSON.parse(alertAction.Configuration);
          const alertTemplateId = config?.AlertTemplateId;

          if (
            alertTemplateId &&
            alertTemplateOptions.some(opt => opt.value === alertTemplateId)
          ) {
            setValue("AlertTemplateId", alertTemplateId);
          } else {
            setValue("AlertTemplateId", null);
          }
        } catch (err) {
          console.warn("Invalid alert config JSON:", alertAction.Configuration);
          setValue("AlertTemplateId", null);
        }
      } else {
        setValue("AlertTemplateId", null);
      }

      setBasicInitialized(true);
    }
  }, [existingTemplate, basicInitialized, alertTemplateOptions, setValue]);

  const [permissionsInitialized, setPermissionsInitialized] = useState(false);
  useEffect(() => {
    if (!permissionsInitialized && users.length > 0 && contactGroups.length > 0) {
      const combinedRecipients = [...users, ...contactGroups].map(rec => ({
        ...rec,
        isAdded: existingTemplate && existingTemplate.permissions
          ? (rec.type === "internaluser"
              ? existingTemplate.permissions.some(p => p.CustomerUserId === rec.id)
              : rec.type === "internalgroup"
              ? existingTemplate.permissions.some(p => p.ContactGroupId === rec.id)
              : false)
          : false
      }));
      setNotSelectedRecipients(combinedRecipients);
      setPermissionsInitialized(true);
    }
  }, [users, contactGroups, existingTemplate, permissionsInitialized]);

  useEffect(() => {
    if (dataUsers) {
      const mappedUsers: MBSRecipient[] = dataUsers.map((user) => ({
        id: user.CustomerUserId,
        name: user.Username || `${user.Email}`,
        email: user.Email,
        mobile: user.Phonenumber,
        deviceToken: user.DeviceToken,
        type: "internaluser",
        uniqueKey: `internaluser-${user.CustomerUserId}`,
        isAdded: false,
        isGroup: false,
      }));
      setUsers(mappedUsers);
    }
  }, [dataUsers]);

  useEffect(() => {
    if (dataContactGroups) {
      const mappedGroups: MBSRecipient[] = dataContactGroups.map((group) => ({
        id: group.ID,
        name: group.Name,
        type: "internalgroup",
        uniqueKey: `internalgroup-${group.ID}`,
        isAdded: false,
        isGroup: true,
        groupMembers: group.ContactGroupUsers?.map((member) => ({
          id: member.ID,
          name: member.UserName,
          email: member.Email,
          mobile: member.MobilePhone,
          deviceToken: member.DeviceToken,
          type: member.Type || "contact",
        })) || [],
      }));
      setContactGroups(mappedGroups);
    }
  }, [dataContactGroups]);

  const getTabs = () => [
    {
      id: 1,
      displayText: "Basic information",
      customClass: "tab-transparent-panel-tab",
      customSelectedClass: "tab-transparent-panel-tab-selected wrap-text",
      clickable: true
    },
    ...(addPermissions
      ? [{
          id: 2,
          displayText: "Permissions",
          customClass: "tab-transparent-panel-tab",
          customSelectedClass: "tab-transparent-panel-tab-selected wrap-text",
          clickable: !!existingTemplate
        }]
      : []),
    {
      id: 3,
      displayText: "Components",
      customClass: "tab-transparent-panel-tab",
      customSelectedClass: "tab-transparent-panel-tab-selected wrap-text",
      clickable: !!existingTemplate
    }
  ];

  const addRecipient = (recipient: MBSRecipient): void => {
    const newNotSelectedRecipients = notSelectedRecipients.map(item => ({
      ...item,
      isAdded: recipient.uniqueKey === item.uniqueKey ? !item.isAdded : item.isAdded
    }));
    setNotSelectedRecipients(newNotSelectedRecipients);
  };

  const onClickRecipient = (recipient: MBSRecipient) => {
    const type = recipient.type;
    const clickedId = recipient.id;

    if (type === "internalgroup") {
      const contactGroupToDisplay = contactGroups.find(p => p.id === clickedId);
      if (!contactGroupToDisplay) return;
      const contactGroupTitle = `${translations.contactgroup} - ${contactGroupToDisplay.name}`;
      const mappedContacts = contactGroupToDisplay.groupMembers.map(p => ({
        id: p.id,
        name: p.name,
        email: p.email,
        mobile: p.mobile,
        deviceToken: p.deviceToken,
        type: p.type
      }) as ContactListModel);

      const contactList = (
        <div>
          <h3>{translations.groupmembers}</h3>
          <ContactList
            translations={translations}
            contacts={mappedContacts}
            showEdit={false}
            showDelete={false}
            showAdd={false}
          />
        </div>
      );

      const modalConf: ModalConfiguration = {
        title: contactGroupTitle,
        body: contactList,
        buttonText: "Ok",
        type: "info",
        position: 'center',
        centerContent: false
      };

      openModal(modalConf);
    } else if (type === "internaluser") {
      const userToDisplay = users.find(p => p.id === clickedId);
      if (!userToDisplay) return;
      const userTitle = `${translations.user} - ${userToDisplay.name}`;

      const modalConf: ModalConfiguration = {
        title: userTitle,
        body: (
          <EditContact
            translations={translations}
            customerId={customer.Id}
            contactId={userToDisplay.id}
            nameInit={userToDisplay.name}
            emailInit={userToDisplay.email}
            mobileInit={userToDisplay.mobile}
            displayOnly
          />
        ),
        hideButton: true,
        position: 'center',
        type: "info"
      };

      openModal(modalConf);
    }
  };

  const tabs = getTabs();

  const onNext = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === selectedTabId);
    const nextTab = tabs[currentIndex + 1];
    if (nextTab) setSelectedTabId(nextTab.id);
  };

  const onBack = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === selectedTabId);
    const prevTab = tabs[currentIndex - 1];
    if (prevTab) setSelectedTabId(prevTab.id);
  };

  const hasBasicChanges = () => {
    if (!existingTemplate) return true;
    const formValues = getValues();
    return (
      templateName !== existingTemplate.Name ||
      templateDescription !== existingTemplate.Description ||
      geoLocation !== existingTemplate.GeoLocationEnabled ||
      confirmBeforeStart !== existingTemplate.ConfirmBeforeStart ||
      addPermissions !== existingTemplate.isPermissionActive ||
      formValues.AlertTemplateId !== getExistingAlertId()
    );
  };

  const getExistingAlertId = () => {
    const alertAction = existingTemplate?.Actions?.find(a => a.EventType === 1);
    if (alertAction) {
      try {
        const config = JSON.parse(alertAction.Configuration);
        return config?.AlertTemplateId || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const hasPermissionChanges = () => {
    if (!addPermissions || selectedTabId !== 2 || !existingTemplate) return false;
    const selectedIds = notSelectedRecipients
      .filter(r => r.isAdded)
      .map(r => r.type === "internaluser" ? `u${r.id}` : `g${r.id}`);
    const existingIds = existingTemplate.permissions?.map(p => p.CustomerUserId ? `u${p.CustomerUserId}` : `g${p.ContactGroupId}`) || [];
    return selectedIds.sort().join(',') !== existingIds.sort().join(',');
  };

  const updateTemplateAsync = (dto) => new Promise((resolve, reject) => {
    updateTemplate(dto, {
      onSuccess: resolve,
      onError: reject
    });
  });

  const createTemplateAsync = (dto) => new Promise((resolve, reject) => {
    createTemplate(dto, {
      onSuccess: resolve,
      onError: reject
    });
  });

  const createSendAlertActionAsync = (dto) => new Promise((resolve, reject) => {
    createSendAlertAction(dto, {
      onSuccess: resolve,
      onError: reject
    });
  });

  const updateSendAlertActionAsync = (dto) => new Promise((resolve, reject) => {
    updateSendAlertAction(dto, {
      onSuccess: resolve,
      onError: reject
    });
  });

  const onNextSave = async () => {
    const formValues = getValues();
    const baseData: EventFlowTemplateFormDto = {
      Name: templateName,
      Description: templateDescription,
      Type: 1,
      GeoLocationEnabled: geoLocation,
      ConfirmBeforeStart: confirmBeforeStart,
      isPermissionActive: addPermissions,
      AlertTemplateId: formValues.AlertTemplateId,
    };

    let permissions: EventFlowTemplatePermissionsDTO[] | undefined = undefined;

    if (addPermissions && selectedTabId === 2) {
      const selected = notSelectedRecipients.filter(r => r.isAdded);
      const existingPermissions = existingTemplate?.permissions || [];
      permissions = selected.map(r => {
        let perm: EventFlowTemplatePermissionsDTO;
        if (r.type === "internaluser") {
          const existing = existingPermissions.find(p => p.CustomerUserId === r.id);
          perm = existing ? { ...existing } : { CustomerUserId: r.id };
        } else {
          const existing = existingPermissions.find(p => p.ContactGroupId === r.id);
          perm = existing ? { ...existing } : { ContactGroupId: r.id };
        }
        if (!('ID' in perm)) {
          (perm as any).ID = 0;
        }
        return perm;
      });
      baseData.permissions = permissions;
    }

    const needsSave = hasBasicChanges() || hasPermissionChanges();
    const needsAlert = formValues.AlertTemplateId !== getExistingAlertId();

    if (!needsSave && !needsAlert) {
      onNext();
      return;
    }

    try {
      if (existingTemplate) {
        let template = existingTemplate;
        if (needsSave) {
          const updateDto: UpdateEventFlowTemplateDto = {
            ...existingTemplate,
            ...baseData,
            permissions: permissions ?? existingTemplate.permissions ?? [],
          };
          template = await updateTemplateAsync(updateDto);
        }

        if (needsAlert) {
          const alertTemplateId = formValues.AlertTemplateId;
          if (alertTemplateId) {
            const existingAction = template.Actions?.find(a => a.EventType === 1 && a.ActionType === 1);
            if (existingAction) {
              await updateSendAlertActionAsync({
                Id: existingAction.Id,
                EventFlowTemplateId: template.Id,
                AlertTemplateId: alertTemplateId
              });
            } else {
              await createSendAlertActionAsync({
                EventFlowTemplateId: template.Id,
                AlertTemplateId: alertTemplateId
              });
            }
          }
        }

        onNext();
      } else {
        const createDto: CreateEventFlowTemplateDto = {
          ...baseData,
          CustomerId: customer.Id,
        };
        const newTemplate = await createTemplateAsync(createDto);

        const alertTemplateId = formValues.AlertTemplateId;
        if (alertTemplateId) {
          await createSendAlertActionAsync({
            EventFlowTemplateId: newTemplate.Id,
            AlertTemplateId: alertTemplateId
          });
        }

        setSearchParams({ templateId: newTemplate.Id.toString() });
      }
    } catch (err) {
      console.error("Save failed", err);
      onNext();
    }
  };

  const renderTabBody = () => {
    switch (selectedTabId) {
      case 1:
        return (
          <IncidentBasicInformation
            control={control}
            onGeoLocation={setGeoLocation}
            onAddPermissions={setAddPermissions}
            onConfirmBeforeStart={setConfirmBeforeStart}
            translations={translations}
            name={templateName}
            description={templateDescription}
            alertTemplateOptions={alertTemplateOptions ?? []}
            enable={false}
            confirmbeforestartIsChecked={confirmBeforeStart}
            addpermissionsIsChecked={addPermissions}
            geolocationIsChecked={geoLocation}
            handleNameChange={(e) => setTemplateName(e.target.value)}
            handleDescriptionChange={(e) => setTemplateDescription(e.target.value)}
          />
        );
      case 2:
        return (
          <TemplatePermissionSelector
            translations={translations}
            recipients={notSelectedRecipients}
            addRecipient={addRecipient}
            clickRecipient={onClickRecipient}
          />
        );
      case 3:
        return existingTemplate ? <CreateComponents eventFlowTemplate={existingTemplate} /> : <Spinner isGlobal />;
    }
  };

  return (
    <ComponentWrapper active={8} subMenuActive={5}>
      <div className="pbs-wrapper">
        <h1 className="heading">{translations.tmpCreate || "Create incident template"}</h1>
        <div style={{ width: "80rem", backgroundColor: "#E0E1DD", height: "5px" }}>
          <div style={{ backgroundColor: "#00A098", width: `${(selectedTabId / tabs.length) * 80}rem`, height: "5px" }} />
        </div>
        <div className="container">
          <TabPanel tabs={tabs} onClickTab={(id) => setSelectedTabId(id)} clickable selectedTabId={selectedTabId} customClass="tab-solid-panel-tms" />
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', width: '100%', paddingBottom: "6rem" }}> {renderTabBody()} </div>

          {/* Sticky button row */}
          <div
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#fff",
              padding: "20px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              borderTop: "1px solid #ccc",
              marginTop: "auto",
              zIndex: 10
            }}
          >
            {selectedTabId > 1 && (
              <Button
                buttonStyle="btn--primary"
                buttonSize="btn--medium"
                onClick={onBack}
              >
                {translations.back || "Back"}
              </Button>
            )}
            {selectedTabId < tabs.length && (
              <div style={{ marginLeft: 20 }}>
                <Button
                  buttonStyle="btn--primary"
                  buttonSize="btn--medium"
                  onClick={onNextSave}
                >
                  {translations.nextBox || "Next"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </ComponentWrapper>
  );
};

export default CreateEventFlowTemplate;